import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { ViewEventData } from '@databases/analytics/src/types';
import { useFlaggingCallback } from '@databases/flags';
import Z_INDEX from '@databases/layout/z-index';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseView } from '@databases/types/database/view';
import {
  HALF_ROW_SELECT_BUTTON_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
} from '@databases/utils/constants';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { getHiddenFields } from '@databases/utils/database/view/view';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import useView from '@databases/views/useView';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { TABLE_CLASSNAMES } from '../../../../constants';
import {
  DraggingIem,
  DragOverItem,
  DragType,
  useDragItems,
  HorizontalPositions,
} from '../../../drag-utils';
import { RefMapWithSizes, useRefMapWithSizes } from '../../../lazy-render';
import { useScope } from '../../Scope';
import { IGNORED_TABLE_ELEMENT_CLASS, RESIZABLE } from '../../TableResizer';

import AddFieldButton from './AddFieldButton';
import TableLayoutHeaderCell, { Th } from './TableLayoutHeaderCell';
import { DRAG_ITEM, ADD_ITEM } from './TableLayoutRows';
export interface TableLayoutHeaderRowProps {
  contentId: string;
  canEditContent: boolean;
  canEditFields: boolean;
  couldEditFields: boolean;
  cantEditInPublish: boolean;
  isContentView: boolean;
  removeExtraActions: boolean;
  containerLeft: number;
  containerHeight: number;
  handleDeleteView: (
    viewId: string,
    source: ViewEventData['source'],
    onCloseAdditional?: () => void
  ) => void;
  handleCopyViewLink: (view: DatabaseView) => boolean;
}

const TR = styled.tr`
  z-index: 2;

  padding-top: 10px;
`;

const TableLayoutHeaderRow = React.memo(
  React.forwardRef<HTMLTableSectionElement | null, TableLayoutHeaderRowProps>(
    function TableLayoutHeaderRow(
      {
        contentId,
        canEditContent,
        canEditFields,
        couldEditFields,
        cantEditInPublish,
        isContentView,
        removeExtraActions,
        containerHeight,
        containerLeft,
        handleDeleteView,
        handleCopyViewLink,
      },
      tableBodyRef
    ) {
      const { view } = useView();
      const scope = useScope();
      const [fieldIds, isLocked, hiddenFields] = defaultStoreUtils.useEqualSelector(
        (state) => {
          const databaseDraftView = selectDatabaseDraftByContentId(state, {
            contentId,
            scope,
            viewSettings: view.settings,
            includeDeleted: true,
          });
          const fieldIds = databaseDraftView.fields;

          return [
            fieldIds,
            databaseDraftView.meta.isLocked,
            getHiddenFields(view.settings),
          ] as const;
        }
      );

      const [dragOver, setDragOver] = useState<DragOverItem>();
      const columnIds = useMemo(() => {
        const result = [...fieldIds];

        if (dragOver) {
          result.splice(dragOver.idx, 0, DRAG_ITEM);
        }

        if (!(removeExtraActions && cantEditInPublish && isContentView)) {
          result.push(ADD_ITEM);
        }

        return result;
      }, [fieldIds, dragOver, removeExtraActions, cantEditInPublish, isContentView]);
      const columnRefMap: RefMapWithSizes<any> = useRefMapWithSizes({
        getPositions: ({ sizes }) => {
          const columnPositions: Record<string, HorizontalPositions> = {};
          let currentLeft = 0;
          for (const fieldId of columnIds) {
            const left = currentLeft;
            const width =
              fieldId === DRAG_ITEM
                ? 0
                : sizes[fieldId]?.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH;
            columnPositions[fieldId] = { left, width };
            currentLeft += width;
          }

          return columnPositions;
        },
      });

      const [draggingItem, setDraggingItem] = useState<DraggingIem>();

      const store = defaultStoreUtils.useStore();
      const dispatch = defaultStoreUtils.useDispatch();
      const client = useAnalytics();
      const onDrop = useFlaggingCallback(
        ({ idx, ids }) => {
          batch(() => {
            collabDatabaseBatch(store, contentId, () => {
              for (const fieldId of ids) {
                void dispatch(
                  addChangeToDraft(contentId, scope, {
                    type: DatabaseChangeType.REORDER_FIELD,
                    fieldId: fieldId,
                    newIndex: idx,
                    hiddenFields,
                  })
                );
              }
            });
          });
          void client.sendColumnReorderedEvent({
            contentId: contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        },
        [client, contentId, dispatch, hiddenFields, scope, store]
      );

      const onDragOver = useFlaggingCallback((val: DragOverItem) => {
        setDragOver(val);

        if (!val?.idx) {
          return;
        }
      }, []);

      const {
        handleDragLeave,
        handleDragEnter,
        handleDragEnd,
        handleDrag,
        handleDrop,
        handleDragOver,
      } = useDragItems({
        dragDirection: 'horizontal',
        ids: fieldIds,
        rowOrColIds: columnIds,
        contentId,
        positions: columnRefMap.positions,
        dragType: DragType.FIELD_COLUMNS,
        onDrop,
        onDragOver,
        onSetDraggingItem: setDraggingItem,
        topOrLeftPadding: 50 - containerLeft,
      });

      return (
        <TR
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {removeExtraActions && cantEditInPublish && isContentView
            ? null
            : // Placeholder for the row selectButton column
              canEditContent && (
                <th
                  className={IGNORED_TABLE_ELEMENT_CLASS}
                  style={{
                    position: 'sticky',
                    width: `${HALF_ROW_SELECT_BUTTON_WIDTH}px`,
                    background: token('elevation.surface', '#FFFFF'),
                    left: 0,
                    zIndex: Z_INDEX.ROW_SELECT_BUTTON,
                  }}
                />
              )}
          {columnIds.map((fieldId) =>
            fieldId !== DRAG_ITEM && fieldId !== ADD_ITEM ? (
              <TableLayoutHeaderCell
                key={fieldId}
                canEditFields={canEditFields}
                cantEditInPublish={cantEditInPublish}
                couldEditFields={couldEditFields}
                isContentView={isContentView}
                removeExtraActions={removeExtraActions}
                contentId={contentId}
                fieldId={fieldId}
                currentViewId={view.id}
                className={RESIZABLE}
                ref={{
                  current: {
                    tableBodyRef: tableBodyRef,
                    headerFieldRef: columnRefMap.refs[fieldId],
                  },
                }}
                isFirstInRow={fieldIds[0] === fieldId}
                isLastInRow={fieldIds[fieldIds.length - 1] === fieldId}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                isDraggingActive={!!draggingItem}
                containerHeight={containerHeight}
                isDragOver={
                  !!draggingItem && dragOver?.idx === columnIds.indexOf(fieldId) - 1
                }
                handleCopyViewLink={handleCopyViewLink}
                handleDeleteView={handleDeleteView}
              />
            ) : (
              fieldId !== DRAG_ITEM &&
              fieldId === ADD_ITEM && (
                <Th
                  key={fieldId}
                  className={classNames([TABLE_CLASSNAMES.ADD_FIELD], {
                    [IGNORED_TABLE_ELEMENT_CLASS]: true,
                  })}
                  containerHeight={containerHeight}
                  isDragOver={dragOver?.idx === columnIds.indexOf(fieldId) - 1}
                >
                  {isLocked && couldEditFields ? (
                    <Tooltip content={() => 'Structure is locked'} position="top">
                      <AddFieldButton
                        contentId={contentId}
                        currentViewId={view.id}
                        isDisabled
                      />
                    </Tooltip>
                  ) : canEditFields ? (
                    <AddFieldButton contentId={contentId} currentViewId={view.id} />
                  ) : null}
                </Th>
              )
            )
          )}
        </TR>
      );
    }
  )
);

export default TableLayoutHeaderRow;
